import L from "leaflet";
import "leaflet-routing-machine";

document.addEventListener("DOMContentLoaded", function () {
  const mapContainer = document.getElementById("mapContainerCitymap");
  const dataElement = document.getElementById("map-entities-data");

  if (mapContainer && dataElement) {
    const entities = JSON.parse(dataElement.textContent);

    // i18n: strings injected by the template (window.MAP_I18N) take priority.
    // If MAP_I18N is missing a key (dev / mis-rendered template), we fall back to a
    // built-in dictionary keyed by the page language. French is the source language,
    // EN and DE are also covered; any other locale falls back to FR.
    const i18n = window.MAP_I18N || {};
    const lang = (document.documentElement.lang || "fr").slice(0, 2).toLowerCase();
    const FALLBACK_STRINGS = {
      start: { fr: "Départ", en: "Start", de: "Start" },
      arrival: { fr: "Arrivée", en: "Arrival", de: "Ankunft" },
      stop: { fr: "Étape", en: "Stop", de: "Zwischenstopp" },
      by_car: { fr: "En voiture", en: "By car", de: "Mit dem Auto" },
      on_foot: { fr: "À pied", en: "On foot", de: "Zu Fuß" },
      add_to_itinerary: { fr: "Ajouter à l'itinéraire", en: "Add to Itinerary", de: "Zur Route hinzufügen" },
      learn_more: { fr: "En savoir plus", en: "Learn more", de: "Mehr erfahren" },
      move_up: { fr: "Déplacer vers le haut", en: "Move up", de: "Nach oben" },
      move_down: { fr: "Déplacer vers le bas", en: "Move down", de: "Nach unten" },
      delete: { fr: "Supprimer", en: "Delete", de: "Löschen" },
      direct_line: { fr: "(ligne directe)", en: "(direct line)", de: "(direkte Linie)" },
      services_unavailable: { fr: "(tous les services indisponibles)", en: "(all services unavailable)", de: "(alle Dienste nicht verfügbar)" },
      as_the_crow_flies: { fr: "(distance à vol d'oiseau)", en: "(as the crow flies)", de: "(Luftlinie)" },
      routing_unavailable_msg: {
        fr: "Le calcul d'itinéraire est momentanément indisponible",
        en: "Route calculation is temporarily unavailable",
        de: "Routenberechnung vorübergehend nicht verfügbar",
      },
      via_ors: { fr: "Via OpenRouteService", en: "Via OpenRouteService", de: "Via OpenRouteService" },
      about: { fr: "environ", en: "about", de: "etwa" },
      minutes_short: { fr: "min", en: "min", de: "Min" },
      calculating_route: { fr: "Calcul de l'itinéraire…", en: "Calculating route…", de: "Routenberechnung…" },
      open_itinerary_panel: { fr: "Ouvrir l'itinéraire", en: "Open itinerary", de: "Route öffnen" },
      use_my_location: { fr: "Utiliser ma position", en: "Use my location", de: "Meinen Standort verwenden" },
      my_location: { fr: "Ma position", en: "My location", de: "Mein Standort" },
      locating: { fr: "Localisation…", en: "Locating…", de: "Standortbestimmung…" },
      geo_denied: { fr: "Autorisation de localisation refusée", en: "Location permission denied", de: "Standortberechtigung verweigert" },
      geo_unavailable: { fr: "La localisation n'est pas disponible sur cet appareil", en: "Location is not available on this device", de: "Standort auf diesem Gerät nicht verfügbar" },
    };
    const t = (key, inlineFallback) => {
      if (i18n[key] != null) return i18n[key];
      const dict = FALLBACK_STRINGS[key];
      if (dict) return dict[lang] || dict.fr || inlineFallback;
      return inlineFallback;
    };

    // HTML escape for values interpolated into innerHTML or attributes.
    const escapeHtml = (value) => {
      if (value == null) return "";
      return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
    };

    // Coordinate helpers: tolerate strings, accept lat/lng = 0, compare with tolerance.
    const isValidLatLng = (lat, lng) => {
      const flat = parseFloat(lat);
      const flng = parseFloat(lng);
      return Number.isFinite(flat) && Number.isFinite(flng);
    };
    const sameLocation = (a, b) => {
      if (!a || !b) return false;
      return Math.abs(parseFloat(a.lat) - parseFloat(b.lat)) < 1e-6 && Math.abs(parseFloat(a.lng) - parseFloat(b.lng)) < 1e-6;
    };

    const map = L.map(mapContainer, {
      scrollWheelZoom: false,
      zoomControl: false,
    }).setView([49.6116, 6.1319], 15);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    // Custom scroll handler - requires Ctrl/Cmd key
    const zoomHint = document.getElementById("mapZoomHint");
    const desktopHint = zoomHint?.querySelector(".desktop-hint");
    const mobileHint = zoomHint?.querySelector(".mobile-hint");
    let zoomHintTimeout = null;

    // Detect touch device
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Show appropriate hint based on device
    function showZoomHint() {
      if (zoomHint) {
        if (isTouchDevice && mobileHint && desktopHint) {
          mobileHint.style.display = "flex";
          desktopHint.style.display = "none";
        } else if (desktopHint && mobileHint) {
          desktopHint.style.display = "flex";
          mobileHint.style.display = "none";
        }
        zoomHint.classList.add("visible");
        clearTimeout(zoomHintTimeout);
        zoomHintTimeout = setTimeout(() => {
          zoomHint.classList.remove("visible");
        }, 1500);
      }
    }

    mapContainer.addEventListener(
      "wheel",
      function (e) {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          const delta = e.deltaY > 0 ? -1 : 1;
          map.setZoom(map.getZoom() + delta);
        } else if (!isTouchDevice) {
          // Show hint only on desktop when scrolling without modifier key
          showZoomHint();
        }
      },
      { passive: false },
    );

    // Handle touch events for mobile one-finger scroll detection
    if (isTouchDevice) {
      let touchStartTime = 0;
      let touchStartY = 0;

      mapContainer.addEventListener(
        "touchstart",
        function (e) {
          touchStartTime = Date.now();
          touchStartY = e.touches[0].clientY;
        },
        { passive: true },
      );

      mapContainer.addEventListener(
        "touchmove",
        function (e) {
          // If single finger and vertical movement (page scroll intent)
          if (e.touches.length === 1) {
            const touchY = e.touches[0].clientY;
            const deltaY = Math.abs(touchY - touchStartY);

            // If significant vertical scroll detected, show hint
            if (deltaY > 50 && Date.now() - touchStartTime < 500) {
              showZoomHint();
            }
          }
        },
        { passive: true },
      );
    }

    // ---- Unused basemaps, kept for reference (uncomment the layer AND its addTo below to switch) ----
    // Stadia Alidade Smooth: requires an API key since 2023.
    // var Stadia_AlidadeSmooth = L.tileLayer("https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.{ext}", {
    //   minZoom: 0,
    //   maxZoom: 20,
    //   attribution: '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    //   ext: "png",
    // });

    // CartoDB Voyager: requires an API key now (tiles are watermarked "API KEY REQUIRED").
    // var CartoDB_Voyager = L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
    //   attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    //   subdomains: "abcd",
    //   maxZoom: 20,
    // });

    // L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    //   attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    // }).addTo(map);

    // ---- Active basemap ----
    // Esri World Light Gray Canvas: free, no API key required (attribution mandatory).
    // Note: Esri tile URLs use {z}/{y}/{x} order. Native tiles stop at zoom 16,
    // maxNativeZoom lets Leaflet upscale them up to zoom 20.
    // var Esri_WorldGrayCanvas = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
    //   attribution: 'Tiles &copy; <a href="https://www.esri.com/" target="_blank">Esri</a> &mdash; Esri, DeLorme, NAVTEQ',
    //   maxNativeZoom: 16,
    //   maxZoom: 20,
    // });

    // Esri World Street Map: free, no API key required (attribution mandatory).
    // Native tiles available up to zoom 19 on Luxembourg; upscaled beyond.
    // var Esri_WorldStreetMap = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", {
    //   attribution: 'Tiles &copy; <a href="https://www.esri.com/" target="_blank">Esri</a> &mdash; Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom',
    //   maxNativeZoom: 19,
    //   maxZoom: 20,
    // });

    // OpenStreetMap France: free community tile server, no API key required (OSM attribution mandatory).
    // var OSM_France = L.tileLayer("https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png", {
    //   attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, tiles by <a href="https://www.openstreetmap.fr/" target="_blank">OSM France</a>',
    //   subdomains: "abc",
    //   maxZoom: 20,
    // });

    // OpenStreetMap Deutschland: free community tile server (FOSSGIS e.V.), no API key required (OSM attribution mandatory).
    // var OSM_DE = L.tileLayer("https://tile.openstreetmap.de/{z}/{x}/{y}.png", {
    //   attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, tiles by <a href="https://www.openstreetmap.de/" target="_blank">OSM Deutschland</a>',
    //   maxZoom: 20,
    // });

    // geoportail.lu (ACT, Administration du cadastre et de la topographie): official Luxembourg
    // open data WMTS. No API key, no fees, CC0/CC-BY, four subdomains (wmts1 to wmts4).
    // Native tiles stop at zoom 19, maxNativeZoom lets Leaflet upscale them up to zoom 20.
    // Other styles on the same endpoint, same URL shape: "basemap" (identical render to roadmap),
    // "topomap" (topographic, colourful), "topomap_gray" (monochrome, makes the coloured markers pop).
    // Note: "hybrid" (aerial + street names) and "topo_5k"/"topo_20k" use GLOBAL_WEBMERCATOR_4_V3 instead.
    var Geoportail_Roadmap = L.tileLayer("https://wmts{s}.geoportail.lu/opendata/wmts/roadmap/GLOBAL_WEBMERCATOR/{z}/{x}/{y}.png", {
      attribution: 'Tiles &copy; <a href="https://www.geoportail.lu/" target="_blank">geoportail.lu</a> &mdash; Administration du cadastre et de la topographie',
      subdomains: ["1", "2", "3", "4"],
      maxNativeZoom: 19,
      maxZoom: 20,
    });

    // Stadia_AlidadeSmooth.addTo(map);
    // CartoDB_Voyager.addTo(map);
    // Esri_WorldStreetMap.addTo(map);
    // OSM_France.addTo(map);
    // OSM_DE.addTo(map);
    // Esri_WorldGrayCanvas.addTo(map);
    Geoportail_Roadmap.addTo(map);

    const colorMap = {
      green: "#28a745",
      red: "#dc3545",
      blue1: "#004085",
      blue2: "#17a2b8",
      purple: "#6f42c1",
      orange: "#fd7e14",
      yellow: "#ffc107",
      default: "#333333",
    };

    const createMarkerIcon = (color, number) => {
      const numStr = number != null ? String(number).trim() : "";
      const hasNumber = numStr !== "";
      const markerSize = 45;
      const badgeSize = 24;
      const anchorOffset = Math.round(markerSize / 2);
      const markerHtml = hasNumber
        ? `<div style="position: relative; width: ${markerSize}px; height: ${markerSize + 10}px;">
            <svg viewBox="0 0 24 24" width="${markerSize}" height="${markerSize}" xmlns="http://www.w3.org/2000/svg" style="position: absolute; bottom: 0; left: 0;">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}"/>
            </svg>
            <div style="position: absolute; top: 15px; left: 50%; transform: translateX(-50%); background: white; border-radius: 50%; width: ${badgeSize}px; height: ${badgeSize}px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; color: #333; border: 2px solid ${color}; box-shadow: 0 2px 4px rgba(0,0,0,0.3); z-index: 200">${escapeHtml(numStr)}</div>
          </div>`
        : `<svg viewBox="0 0 24 24" width="${markerSize}" height="${markerSize}" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}"/>
            <circle cx="12" cy="9.5" r="2" fill="white"/>
          </svg>`;

      return L.divIcon({
        html: markerHtml,
        className: "custom-leaflet-marker",
        iconSize: hasNumber ? [markerSize, markerSize + 10] : [markerSize, markerSize],
        iconAnchor: [anchorOffset, markerSize],
        popupAnchor: [0, -markerSize],
      });
    };

    const allMarkers = [];

    entities.forEach((entity) => {
      const lat = entity.lat;
      const lon = entity.lng;

      if (isValidLatLng(lat, lon)) {
        const hexColor = colorMap[entity.themeColorName] || colorMap.default;
        const icon = createMarkerIcon(hexColor, entity.index);

        let truncatedText = "";
        if (entity.text) {
          const maxLength = 150;
          if (entity.text.length > maxLength) {
            truncatedText = entity.text.substring(0, maxLength) + "...";
          } else {
            truncatedText = entity.text;
          }
        }

        const safeName = escapeHtml(entity.name);
        const safeImage = escapeHtml(entity.image);
        const safeUrl = escapeHtml(entity.url);
        const safeText = escapeHtml(truncatedText);
        const learnMoreLabel = escapeHtml(t("learn_more", "Learn more"));
        const addLabel = escapeHtml(t("add_to_itinerary", "Add to Itinerary"));

        const popupContent = `
          <div class="map-popup">
            ${entity.image ? `<img src="${safeImage}" alt="${safeName}"/>` : ""}
            <h3>${safeName}</h3>
            ${truncatedText ? `<div class="map-popup-text">${safeText}</div>` : ""}
            <a class="map-popup-link" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${learnMoreLabel}</a>
            <button type="button" class="route-btn add-to-route">${addLabel}</button>
          </div>
        `;

        // Accessible name must start with any visible text on the marker (the
        // index badge "1", "2", …) to satisfy WCAG label-content-name-mismatch.
        // `entity.index` is map-agnostic: the template resolves whichever CMS field
        // carries the numbering for this map before serialising the JSON.
        const idx = entity.index != null ? String(entity.index).trim() : "";
        const accessibleName = idx && entity.name ? `${idx}. ${entity.name}` : entity.name || idx;

        const marker = L.marker([lat, lon], {
          icon: icon,
          alt: accessibleName,
          title: accessibleName,
        }).bindPopup(popupContent);
        allMarkers.push({ marker, entity });
        marker.addTo(map);

        // Reinforce the accessible name on the marker's DOM element: alt isn't
        // always exposed on a divIcon wrapper, so we set aria-label too.
        const markerEl = marker.getElement();
        if (markerEl && accessibleName) {
          markerEl.setAttribute("aria-label", accessibleName);
          markerEl.setAttribute("role", "button");
        }
      }
    });

    // Initial framing: open on the markers actually present instead of the hardcoded
    // city-centre view set above. A map holding one or two entities (the Kirchberg map)
    // otherwise opens kilometres away from its only marker, and nothing looks plotted.
    // maxZoom stops a single marker from zooming to street level; the setView above
    // remains the fallback when no entity has usable coordinates.
    if (allMarkers.length > 0) {
      const initialBounds = L.latLngBounds(allMarkers.map(({ marker }) => marker.getLatLng()));
      map.fitBounds(initialBounds, { padding: [60, 60], maxZoom: 16 });
    }

    // ==========================================
    // CONFIGURATION ROUTING SERVICES
    // ==========================================
    // ORS API key is injected by the template via window.LCTO_ORS_API_KEY.
    // TODO: move to a backend proxy so the key is never sent to the client.
    const ORS_API_KEY = window.LCTO_ORS_API_KEY || "";

    let waypoints = [];
    // Default routing mode is walking (per client request — the car button is hidden).
    let currentProfile = "walking";
    let routingControl = null;
    let fallbackPolyline = null;
    let orsPolyline = null;
    let osrmFailed = false; // Track if OSRM failed for this calculation
    let userLocationMarker = null; // Set when geolocation is active

    // Custom icon for the "you are here" marker — pulsing blue dot.
    const userLocationIcon = L.divIcon({
      className: "user-location-marker",
      html: '<span class="user-location-pulse" aria-hidden="true"></span><span class="user-location-dot" aria-hidden="true"></span>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    const routeWaypointsContainer = document.getElementById("route-waypoints");
    const clearRouteBtn = document.getElementById("clear-route-btn");
    const instructionsContainer = document.querySelector(".custom-routing-container");

    // Mobile DOM refs (always present in the rendered template; hidden via CSS on desktop).
    const mobileWaypointsContainer = document.getElementById("mobile-route-waypoints");
    const routeNotification = document.getElementById("route-notification");
    const routeNotificationNav = document.getElementById("route-notification-nav");
    const mobileFilterBtn = document.getElementById("mobile-filter-btn");
    const mobileFilterBtnNav = document.getElementById("mobile-filter-btn-nav");
    const filterModal = document.getElementById("mobile-filter-modal");
    const listModal = document.getElementById("mobile-list-modal");

    // Modal helpers usable from any scope (main or mobile-only block).
    const lastFocus = { filter: null, list: null };
    function openModal(modal, key, opener) {
      if (!modal) return;
      modal.classList.add("active");
      modal.setAttribute("aria-hidden", "false");
      if (opener) lastFocus[key] = opener;
      modal.querySelector(".mobile-modal-close")?.focus();
    }
    function closeModal(modal, key) {
      if (!modal) return;
      modal.classList.remove("active");
      modal.setAttribute("aria-hidden", "true");
      lastFocus[key]?.focus?.();
      lastFocus[key] = null;
    }

    function modeLabel() {
      return currentProfile === "driving" ? t("by_car", "En voiture") : t("on_foot", "À pied");
    }

    function setRouteLoading(isLoading) {
      if (!instructionsContainer) return;
      instructionsContainer.classList.toggle("route-loading", isLoading);
      if (isLoading) {
        const label = escapeHtml(t("calculating_route", "Calcul de l'itinéraire en cours…"));
        instructionsContainer.innerHTML = `<span class="route-loading-label">${label}</span>`;
        instructionsContainer.classList.add("has-content");
      }
    }

    function clearRouteSummary() {
      if (!instructionsContainer) return;
      instructionsContainer.innerHTML = "";
      instructionsContainer.classList.remove("has-content", "route-loading");
    }

    // Click/keyboard on the floating route summary opens the panel scrolled to the itinerary.
    // Scrolling happens *inside* the panel (.route-control sits at the bottom of the content),
    // not on the page, so the map stays visible behind the modal.
    function focusItineraryPanel() {
      const isMobileLayout = isTouchDevice || window.innerWidth < 1024;
      const scrollToBottom = (container) => {
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      };

      if (isMobileLayout && filterModal) {
        const wasOpen = filterModal.classList.contains("active");
        if (!wasOpen) openModal(filterModal, "filter");
        const scrollContainer = filterModal.querySelector(".mobile-modal-content");
        // If the modal was just opened, wait for the slide-in animation; otherwise scroll now.
        if (wasOpen) scrollToBottom(scrollContainer);
        else setTimeout(() => scrollToBottom(scrollContainer), 250);
      } else {
        // Desktop: scroll within the panel's box-search rather than the page.
        scrollToBottom(document.querySelector(".map-filter-panel .box-search"));
      }
    }

    if (instructionsContainer) {
      instructionsContainer.addEventListener("click", (e) => {
        if (e.target.closest(".route-summary")) focusItineraryPanel();
      });
      instructionsContainer.addEventListener("keydown", (e) => {
        if ((e.key === "Enter" || e.key === " ") && e.target.closest(".route-summary")) {
          e.preventDefault();
          focusItineraryPanel();
        }
      });
    }

    function getOSRMServiceUrl(profile) {
      const profileMap = {
        driving: "https://routing.openstreetmap.de/routed-car/route/v1",
        walking: "https://routing.openstreetmap.de/routed-foot/route/v1",
      };
      return profileMap[profile] || profileMap.driving;
    }

    function getORSProfile(profile) {
      const profileMap = {
        driving: "driving-car",
        walking: "foot-walking",
      };
      return profileMap[profile] || "driving-car";
    }

    function clearAllRoutes() {
      if (fallbackPolyline) {
        map.removeLayer(fallbackPolyline);
        fallbackPolyline = null;
      }
      if (orsPolyline) {
        map.removeLayer(orsPolyline);
        orsPolyline = null;
      }
    }

    function clearFallbackPolyline() {
      clearAllRoutes();
    }

    function showFallbackRoute() {
      clearAllRoutes();
      setRouteLoading(false);

      if (waypoints.length < 2) return;

      const latlngs = waypoints.map((wp) => L.latLng(wp.lat, wp.lng));
      const color = currentProfile === "driving" ? "#004085" : "#28a745";

      fallbackPolyline = L.polyline(latlngs, {
        color: color,
        weight: 4,
        opacity: 0.7,
        dashArray: "10, 10",
      }).addTo(map);

      // Calculate straight-line distance
      let totalDistance = 0;
      for (let i = 0; i < waypoints.length - 1; i++) {
        totalDistance += L.latLng(waypoints[i].lat, waypoints[i].lng).distanceTo(L.latLng(waypoints[i + 1].lat, waypoints[i + 1].lng));
      }

      const distanceKm = (totalDistance / 1000).toFixed(2);
      const serviceLabel = osrmFailed ? t("services_unavailable", "(tous les services indisponibles)") : "";
      const directLine = t("direct_line", "(ligne directe)");
      const asTheCrowFlies = t("as_the_crow_flies", "(distance à vol d'oiseau)");
      const unavailableMsg = t("routing_unavailable_msg", "Le calcul d'itinéraire précis est momentanément indisponible");

      instructionsContainer.innerHTML = `
        <div class="route-summary route-fallback" role="button" tabindex="0" aria-label="${escapeHtml(t("open_itinerary_panel", "Open itinerary"))}">
          <h3>${escapeHtml(modeLabel())} ${escapeHtml(directLine)} ${escapeHtml(serviceLabel)}</h3>
          <p><strong>${distanceKm} km</strong> ${escapeHtml(asTheCrowFlies)}</p>
          <p class="route-note"><i>⚠ ${escapeHtml(unavailableMsg)}</i></p>
        </div>
      `;
      instructionsContainer.classList.add("has-content");
    }

    // ==========================================
    // OPENROUTESERVICE (ORS) FALLBACK
    // ==========================================

    // Décodeur polyline (algorithme Google Polyline Encoding)
    function decodePolyline(encoded) {
      const points = [];
      let index = 0,
        lat = 0,
        lng = 0;

      while (index < encoded.length) {
        let b,
          shift = 0,
          result = 0;
        do {
          b = encoded.charCodeAt(index++) - 63;
          result |= (b & 0x1f) << shift;
          shift += 5;
        } while (b >= 0x20);
        const dlat = result & 1 ? ~(result >> 1) : result >> 1;
        lat += dlat;

        shift = 0;
        result = 0;
        do {
          b = encoded.charCodeAt(index++) - 63;
          result |= (b & 0x1f) << shift;
          shift += 5;
        } while (b >= 0x20);
        const dlng = result & 1 ? ~(result >> 1) : result >> 1;
        lng += dlng;

        points.push(L.latLng(lat / 1e5, lng / 1e5));
      }

      return points;
    }
    async function fetchORSRoute() {
      if (waypoints.length < 2) return null;
      if (!ORS_API_KEY) return null;

      // ORS attend [longitude, latitude]
      const coordinates = waypoints.map((wp) => [parseFloat(wp.lng), parseFloat(wp.lat)]);
      const profile = getORSProfile(currentProfile);

      const requestBody = {
        coordinates: coordinates,
        instructions: false,
        geometry: true,
        units: "km",
      };

      try {
        const response = await fetch("https://api.openrouteservice.org/v2/directions/" + profile, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + ORS_API_KEY,
            "Content-Type": "application/json",
            Accept: "application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.warn("ORS API error:", response.status, errorText);
          return null;
        }

        const data = await response.json();

        if (!data.routes || !data.routes[0]) {
          console.warn("ORS: No routes found");
          return null;
        }

        return data;
      } catch (error) {
        console.error("ORS fetch error:", error);
        return null;
      }
    }

    function displayORSRoute(data) {
      clearAllRoutes();
      setRouteLoading(false);

      const route = data.routes[0];
      const geometry = route.geometry;

      let latlngs = [];

      if (geometry && geometry.coordinates && Array.isArray(geometry.coordinates)) {
        // Format GeoJSON LineString
        latlngs = geometry.coordinates.map((coord) => {
          // GeoJSON est [longitude, latitude], Leaflet veut [lat, lng]
          return L.latLng(coord[1], coord[0]);
        });
      } else if (typeof geometry === "string") {
        // Format encodé (polyline) - utiliser notre décodeur
        try {
          latlngs = decodePolyline(geometry);
        } catch (e) {
          console.error("Error decoding ORS polyline:", e);
        }
      }

      // Vérifier qu'on a bien des points
      if (latlngs.length === 0) {
        console.warn("No valid coordinates decoded from ORS response");
        return false;
      }

      const color = currentProfile === "driving" ? "#004085" : "#28a745";

      // Créer la polyligne avec plus d'options visibles
      orsPolyline = L.polyline(latlngs, {
        color: color,
        weight: 5,
        opacity: 0.9,
        lineJoin: "round",
      }).addTo(map);

      // Ajuster la vue pour voir toute la route
      map.fitBounds(orsPolyline.getBounds(), { padding: [50, 50] });

      const distanceKm = (route.summary.distance / 1000).toFixed(2);
      const timeMin = Math.round(route.summary.duration / 60);
      const aboutLabel = t("about", "environ");
      const minLabel = t("minutes_short", "min");
      const viaOrs = t("via_ors", "Via OpenRouteService");

      instructionsContainer.innerHTML = `
        <div class="route-summary route-ors" role="button" tabindex="0" aria-label="${escapeHtml(t("open_itinerary_panel", "Open itinerary"))}">
          <h3>${escapeHtml(modeLabel())}</h3>
          <p><strong>${distanceKm} km</strong> · ${escapeHtml(aboutLabel)} <strong>${timeMin} ${escapeHtml(minLabel)}</strong></p>
          <p class="route-note-success"><i>✓ ${escapeHtml(viaOrs)}</i></p>
        </div>
      `;
      instructionsContainer.classList.add("has-content");

      return true;
    }

    async function tryORSFallback() {
      const orsData = await fetchORSRoute();
      if (orsData && displayORSRoute(orsData)) {
        return true;
      }
      return false;
    }

    function initRoutingControl() {
      clearAllRoutes();
      osrmFailed = false;

      if (routingControl) {
        map.removeControl(routingControl);
      }

      if (waypoints.length >= 2) {
        setRouteLoading(true);
      } else {
        clearRouteSummary();
      }

      routingControl = L.Routing.control({
        waypoints: waypoints.map((wp) => L.latLng(wp.lat, wp.lng)),
        routeWhileDragging: false,
        show: false,
        addWaypoints: false,
        createMarker: function () {
          return null;
        },
        router: L.Routing.osrmv1({
          serviceUrl: getOSRMServiceUrl(currentProfile),
          profile: currentProfile,
        }),
        lineOptions: {
          styles: [{ color: currentProfile === "driving" ? "#004085" : "#28a745", weight: 5, opacity: 0.8 }],
        },
      })
        .on("routesfound", function (e) {
          clearAllRoutes();
          osrmFailed = false;
          const routes = e.routes;
          if (!routes || !routes.length) {
            // OSRM returned empty, try ORS
            tryORSFallback().then((success) => {
              if (!success) showFallbackRoute();
            });
            return;
          }

          // Validate route data
          const route = routes[0];
          if (!route.summary || typeof route.summary.totalDistance === "undefined") {
            console.warn("Invalid OSRM data, trying ORS fallback...");
            tryORSFallback().then((success) => {
              if (!success) showFallbackRoute();
            });
            return;
          }

          setRouteLoading(false);
          const summary = route.summary;

          const distanceKm = (summary.totalDistance / 1000).toFixed(2);
          const timeMin = Math.round(summary.totalTime / 60);
          const aboutLabel = t("about", "environ");
          const minLabel = t("minutes_short", "min");

          instructionsContainer.innerHTML = `
            <div class="route-summary" role="button" tabindex="0" aria-label="${escapeHtml(t("open_itinerary_panel", "Open itinerary"))}">
              <h3>${escapeHtml(modeLabel())}</h3>
              <p><strong>${distanceKm} km</strong> · ${escapeHtml(aboutLabel)} <strong>${timeMin} ${escapeHtml(minLabel)}</strong></p>
            </div>
          `;
          instructionsContainer.classList.add("has-content");
        })
        .on("routingerror", async function (e) {
          console.error("OSRM routing error, trying ORS fallback...", e);
          osrmFailed = true;

          // Remove the failed routing control
          if (routingControl) {
            map.removeControl(routingControl);
            routingControl = null;
          }

          // Try ORS as fallback
          const orsSuccess = await tryORSFallback();
          if (!orsSuccess) {
            // If ORS also fails, show straight line
            showFallbackRoute();
          }
        })
        .addTo(map);
    }

    // Initialize routing control
    initRoutingControl();

    function updateRoute() {
      if (routingControl) {
        const validWaypoints = waypoints.map((wp) => L.latLng(wp.lat, wp.lng));
        routingControl.getPlan().setWaypoints(validWaypoints);
      }
    }

    function updateRouteNotification() {
      const hasWaypoints = waypoints.length > 0;
      if (routeNotification) routeNotification.style.display = hasWaypoints ? "block" : "none";
      if (routeNotificationNav) routeNotificationNav.style.display = hasWaypoints ? "block" : "none";
      mobileFilterBtn?.classList.toggle("has-notification", hasWaypoints);
      mobileFilterBtnNav?.classList.toggle("has-notification", hasWaypoints);
    }

    function syncMobileRouteSummary() {
      const mobileRouteControl = document.querySelector(".mobile-modal-content .route-control");
      if (!mobileRouteControl || !instructionsContainer) return;
      const summary = instructionsContainer.querySelector(".route-summary");
      const existing = mobileRouteControl.querySelector(".route-summary");
      if (summary && !existing) {
        const clone = summary.cloneNode(true);
        mobileRouteControl.insertBefore(clone, mobileRouteControl.querySelector("h4"));
      } else if (summary && existing) {
        existing.innerHTML = summary.innerHTML;
      }
    }

    // The geolocation waypoint, when present, is always pinned at index 0.
    function isGeoActive() {
      return waypoints.length > 0 && waypoints[0].isCurrentLocation === true;
    }

    // Re-frames the map after a waypoint is added or geolocation activates.
    //   - If a route exists (≥ 2 waypoints): fit those waypoints.
    //   - Else if geo is active alone: fit the user position + every currently-visible
    //     POI marker (filters are honoured via map.hasLayer).
    function autoFitMap() {
      if (waypoints.length >= 2) {
        const bounds = L.latLngBounds(waypoints.map((wp) => [parseFloat(wp.lat), parseFloat(wp.lng)]));
        map.fitBounds(bounds, { padding: [80, 80] });
      } else if (isGeoActive()) {
        const points = [[parseFloat(waypoints[0].lat), parseFloat(waypoints[0].lng)]];
        allMarkers.forEach(({ marker }) => {
          if (map.hasLayer(marker)) {
            const ll = marker.getLatLng();
            points.push([ll.lat, ll.lng]);
          }
        });
        if (points.length > 1) {
          map.fitBounds(L.latLngBounds(points), { padding: [60, 60] });
        }
      }
    }

    // Recenter the map on every POI currently shown (filters honoured). Used after
    // turning geolocation off so the user re-discovers the city without their dot.
    function fitToVisiblePois() {
      const points = [];
      allMarkers.forEach(({ marker }) => {
        if (map.hasLayer(marker)) {
          const ll = marker.getLatLng();
          points.push([ll.lat, ll.lng]);
        }
      });
      if (points.length > 0) {
        map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
      }
    }

    function moveWaypoint(from, to) {
      if (from === to || from < 0 || to < 0 || from >= waypoints.length || to >= waypoints.length) return;
      // Forbid any move that would dislodge the locked geolocation waypoint at index 0.
      if (isGeoActive() && (from === 0 || to === 0)) return;
      const [item] = waypoints.splice(from, 1);
      waypoints.splice(to, 0, item);
      updateRoute();
      updateWaypointsUI();
    }

    function showUserLocationMarker(lat, lng) {
      if (userLocationMarker) {
        userLocationMarker.setLatLng([lat, lng]);
      } else {
        userLocationMarker = L.marker([lat, lng], { icon: userLocationIcon, interactive: false, keyboard: false });
        userLocationMarker.addTo(map);
      }
    }

    function hideUserLocationMarker() {
      if (userLocationMarker) {
        map.removeLayer(userLocationMarker);
        userLocationMarker = null;
      }
    }

    function showGeoMessage(text) {
      document.querySelectorAll(".geo-message").forEach((el) => {
        el.textContent = text;
        el.classList.add("visible");
      });
      // Clear after a few seconds so the panel doesn't stay cluttered.
      setTimeout(() => {
        document.querySelectorAll(".geo-message").forEach((el) => {
          el.classList.remove("visible");
          el.textContent = "";
        });
      }, 4000);
    }

    function syncGeolocateBtnState(loading) {
      const active = isGeoActive();
      const labelText = loading ? t("locating", "Locating…") : t("use_my_location", "Use my location");
      document.querySelectorAll(".geolocate-btn").forEach((btn) => {
        btn.classList.toggle("active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
        btn.disabled = !!loading;
        // Keep visible-only/title and screen-reader label in sync.
        btn.setAttribute("aria-label", labelText);
        btn.setAttribute("title", labelText);
        const labelEl = btn.querySelector(".geolocate-label");
        if (labelEl) labelEl.textContent = labelText;
        // Spin the icon while we wait for the browser's geolocation callback.
        const icon = btn.querySelector(".geolocate-icon");
        if (icon) icon.classList.toggle("fa-spin", !!loading);
      });
    }

    function enableGeolocation(lat, lng) {
      if (isGeoActive()) {
        waypoints[0].lat = lat;
        waypoints[0].lng = lng;
      } else {
        waypoints.unshift({
          lat,
          lng,
          name: t("my_location", "My location"),
          isCurrentLocation: true,
        });
      }
      showUserLocationMarker(lat, lng);
      syncGeolocateBtnState(false);
      updateRoute();
      updateWaypointsUI();
      autoFitMap();
    }

    function disableGeolocation() {
      if (isGeoActive()) waypoints.shift();
      hideUserLocationMarker();
      syncGeolocateBtnState(false);
      updateRoute();
      updateWaypointsUI();
      // Recenter the map: if a route is still active, frame it; otherwise show POIs.
      if (waypoints.length >= 2) {
        autoFitMap();
      } else {
        fitToVisiblePois();
      }
    }

    function requestGeolocation() {
      if (!("geolocation" in navigator)) {
        showGeoMessage(t("geo_unavailable", "Location is not available on this device"));
        return;
      }
      syncGeolocateBtnState(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => enableGeolocation(pos.coords.latitude, pos.coords.longitude),
        (err) => {
          syncGeolocateBtnState(false);
          const denied = err && err.code === err.PERMISSION_DENIED;
          showGeoMessage(denied ? t("geo_denied", "Location permission denied") : t("geo_unavailable", "Location is not available on this device"));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    }

    document.querySelectorAll(".geolocate-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (isGeoActive()) disableGeolocation();
        else requestGeolocation();
      });
    });

    function renderWaypointItem(wp, index, total) {
      const isGeo = wp.isCurrentLocation === true;
      const geoActive = isGeoActive();
      const type = isGeo ? t("my_location", "My location") : index === 0 ? t("start", "Départ") : index === total - 1 ? t("arrival", "Arrivée") : t("stop", "Étape");
      const isFirst = index === 0;
      const isLast = index === total - 1;
      // The geo waypoint is locked at #1: its down arrow is disabled, and the
      // waypoint immediately after it can't move up (would swap into position 0).
      const upDisabled = isFirst || (geoActive && index === 1);
      const downDisabled = isLast || isGeo;
      const upLabel = escapeHtml(t("move_up", "Move up"));
      const downLabel = escapeHtml(t("move_down", "Move down"));
      const deleteLabel = escapeHtml(t("delete", "Supprimer"));

      const el = document.createElement("div");
      el.className = "waypoint-item" + (isGeo ? " waypoint-geo" : "");
      el.dataset.index = index;
      const textInner = isGeo ? `<i class="fal fa-location-arrow" aria-hidden="true"></i> <strong>${escapeHtml(type)}</strong>` : `<strong>${escapeHtml(type)}:</strong> ${escapeHtml(wp.name)}`;
      el.innerHTML = `
        <div class="move-controls">
          <button type="button" class="move-btn move-up-btn" aria-label="${upLabel}" title="${upLabel}"${upDisabled ? " disabled" : ""}>
            <i class="fal fa-chevron-up" aria-hidden="true"></i>
          </button>
          <button type="button" class="move-btn move-down-btn" aria-label="${downLabel}" title="${downLabel}"${downDisabled ? " disabled" : ""}>
            <i class="fal fa-chevron-down" aria-hidden="true"></i>
          </button>
        </div>
        <span class="wp-text">${textInner}</span>
        <button type="button" class="delete-wp-btn" aria-label="${deleteLabel}" title="${deleteLabel}">×</button>
      `;
      el.querySelector(".move-up-btn").addEventListener("click", () => moveWaypoint(index, index - 1));
      el.querySelector(".move-down-btn").addEventListener("click", () => moveWaypoint(index, index + 1));
      el.querySelector(".delete-wp-btn").addEventListener("click", () => {
        // The × on the geolocation waypoint disables geolocation rather than just removing it.
        if (waypoints[index]?.isCurrentLocation) {
          disableGeolocation();
          return;
        }
        waypoints.splice(index, 1);
        updateRoute();
        updateWaypointsUI();
      });
      return el;
    }

    function updateWaypointsUI() {
      [routeWaypointsContainer, mobileWaypointsContainer].forEach((container) => {
        if (!container) return;
        container.innerHTML = "";
        waypoints.forEach((wp, index) => {
          if (wp) container.appendChild(renderWaypointItem(wp, index, waypoints.length));
        });
      });
      updateRouteNotification();
      syncMobileRouteSummary();
      syncGeolocateBtnState(false);
    }

    // Keyboard a11y: remember the element that had focus when a popup opens,
    // so we can return focus there when the popup closes (Esc / close button).
    let popupReturnFocus = null;
    let popupFocusTrapHandler = null;

    const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function installPopupFocusTrap(popupContainer) {
      const focusables = Array.from(popupContainer.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return null;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const handler = (ev) => {
        if (ev.key !== "Tab") return;
        // Only trap while focus is inside this popup.
        if (!popupContainer.contains(document.activeElement)) return;
        if (ev.shiftKey && document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      };
      document.addEventListener("keydown", handler);
      return handler;
    }

    map.on("popupopen", function (e) {
      const popupNode = e.popup._contentNode;
      const marker = e.popup._source;
      const found = allMarkers.find((m) => m.marker === marker);
      if (!found) return;
      const entityData = found.entity;

      // Capture trigger element + move focus into the popup. Wait one frame so
      // Leaflet has finished inserting the popup container into the DOM.
      const trigger = document.activeElement;
      if (trigger instanceof HTMLElement && trigger !== document.body) {
        popupReturnFocus = trigger;
      }
      requestAnimationFrame(() => {
        const popupContainer = e.popup.getElement?.() || e.popup._container;
        if (!popupContainer) return;
        const closeBtn = popupContainer.querySelector(".leaflet-popup-close-button");
        if (closeBtn) {
          closeBtn.focus();
        } else if (popupNode) {
          popupNode.setAttribute("tabindex", "-1");
          popupNode.focus();
        }
        // Install Tab/Shift+Tab trap so the focus cycles inside the popup
        // until the user explicitly closes it (Esc, ×, or programmatic close).
        if (popupFocusTrapHandler) {
          document.removeEventListener("keydown", popupFocusTrapHandler);
        }
        popupFocusTrapHandler = installPopupFocusTrap(popupContainer);
      });

      const addButton = popupNode.querySelector(".add-to-route");
      if (!addButton) return;
      addButton.addEventListener("click", () => {
        const newWaypoint = { lat: entityData.lat, lng: entityData.lng, name: entityData.name };

        // Prevent duplicates
        if (waypoints.some((wp) => sameLocation(wp, newWaypoint))) {
          map.closePopup();
          return;
        }

        if (waypoints.length < 2) {
          waypoints.push(newWaypoint);
        } else {
          waypoints.splice(waypoints.length - 1, 0, newWaypoint);
        }

        updateRoute();
        updateWaypointsUI();
        map.closePopup();
        autoFitMap();
      });
    });

    map.on("popupclose", function () {
      // Tear down the focus trap installed on popupopen.
      if (popupFocusTrapHandler) {
        document.removeEventListener("keydown", popupFocusTrapHandler);
        popupFocusTrapHandler = null;
      }
      // Return focus to the element that triggered the popup, if it's still in the DOM.
      if (popupReturnFocus && document.contains(popupReturnFocus) && typeof popupReturnFocus.focus === "function") {
        popupReturnFocus.focus();
      }
      popupReturnFocus = null;
    });

    if (clearRouteBtn) {
      clearRouteBtn.addEventListener("click", () => {
        waypoints = [];
        clearFallbackPolyline();
        hideUserLocationMarker();
        if (routingControl) {
          map.removeControl(routingControl);
          routingControl = null;
        }
        initRoutingControl();
        updateWaypointsUI();
        clearRouteSummary();
      });
    }

    const searchInput = document.getElementById("map-search-input");
    const themeTags = document.querySelectorAll(".map-filter-panel .tag");
    let currentSearch = "";
    let currentTheme = "all";

    function filterMarkers() {
      const searchLower = currentSearch.toLowerCase();

      allMarkers.forEach(({ marker, entity }) => {
        const themeMatch = currentTheme === "all" || entity.themeColorName === currentTheme;
        const searchMatch = !searchLower || entity.name.toLowerCase().includes(searchLower);

        if (themeMatch && searchMatch) {
          if (!map.hasLayer(marker)) {
            marker.addTo(map);
          }
        } else {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        }
      });
    }

    searchInput.addEventListener("input", (e) => {
      currentSearch = e.target.value;
      filterMarkers();
    });

    function startBounce(marker) {
      if (marker.isBouncing) return;
      // Don't bounce only if THIS marker has the popup open
      if (map._popup && map._popup.isOpen() && map._popup._source === marker) return;
      marker.isBouncing = true;
      marker.setZIndexOffset(1000);
      marker._originalLatLng = marker.getLatLng(); // Store for reset
      const originalLatLng = marker._originalLatLng;
      const bounceHeight = 0.0003;
      const duration = 1200; // Slower for continuous effect

      let startTime = null;

      const animate = (currentTime) => {
        if (!marker.isBouncing) return; // Stop animation loop
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / duration;
        const newLat = originalLatLng.lat + Math.abs(Math.sin(progress * 2 * Math.PI)) * bounceHeight;
        marker.setLatLng([newLat, originalLatLng.lng]);
        marker.animationFrameId = requestAnimationFrame(animate);
      };

      marker.animationFrameId = requestAnimationFrame(animate);
    }

    function stopBounce(marker) {
      if (!marker.isBouncing) return;
      marker.isBouncing = false;
      marker.setZIndexOffset(0);
      if (marker.animationFrameId) {
        cancelAnimationFrame(marker.animationFrameId);
        marker.animationFrameId = null;
      }
      // Reset position immediately to original location
      if (marker._originalLatLng) {
        marker.setLatLng(marker._originalLatLng);
      }
    }

    const entityListItems = document.querySelectorAll(".entity-list-item");
    entityListItems.forEach((item) => {
      const findTarget = () => {
        const lat = item.dataset.lat;
        const lng = item.dataset.lng;
        if (!isValidLatLng(lat, lng)) return null;
        return allMarkers.find(({ entity }) => sameLocation(entity, { lat, lng })) || null;
      };

      item.addEventListener("click", (e) => {
        e.preventDefault();
        const target = findTarget();
        if (!target) return;
        map.setView([parseFloat(item.dataset.lat), parseFloat(item.dataset.lng)], 17);
        stopBounce(target.marker);
        target.marker.openPopup();
      });

      item.addEventListener("mouseenter", () => {
        const target = findTarget();
        if (target) startBounce(target.marker);
      });

      item.addEventListener("mouseleave", () => {
        const target = findTarget();
        if (target) stopBounce(target.marker);
      });
    });

    // ==========================================
    // ROUTE MODE (driving / walking)
    // Listeners attached in main scope so desktop buttons work
    // even when the mobile-only block below does not run.
    // ==========================================
    function setRouteMode(mode) {
      currentProfile = mode;
      osrmFailed = false;
      document.querySelectorAll(".route-mode-btn").forEach((btn) => {
        const isActive = btn.dataset.mode === mode;
        btn.classList.toggle("active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
      initRoutingControl();
    }

    document.querySelectorAll(".route-mode-btn").forEach((btn) => {
      btn.addEventListener("click", () => setRouteMode(btn.dataset.mode));
    });

    // Theme tags act as toggle buttons. Sync .active + aria-pressed across desktop & mobile copies.
    function setThemeFilter(theme) {
      currentTheme = theme;
      document.querySelectorAll(".tag[data-theme]").forEach((tag) => {
        const isActive = tag.dataset.theme === theme;
        tag.classList.toggle("active", isActive);
        tag.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
      filterMarkers();
    }

    document.querySelectorAll(".tag[data-theme]").forEach((tag) => {
      tag.addEventListener("click", (e) => {
        e.preventDefault();
        setThemeFilter(tag.dataset.theme);
      });
      tag.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setThemeFilter(tag.dataset.theme);
        }
      });
    });

    // ==========================================
    // MOBILE INTERACTIONS
    // ==========================================
    if (isTouchDevice || window.innerWidth < 1024) {
      // mobileFilterBtn, mobileFilterBtnNav, filterModal, listModal, openModal, closeModal,
      // routeNotification* and mobileWaypointsContainer are declared in the main scope above.
      const mobileListBtn = document.getElementById("mobile-list-btn");
      const mobileScrollFooterBtn = document.getElementById("mobile-scroll-footer-btn");
      const closeFilterBtn = document.getElementById("close-filter-modal");
      const closeListBtn = document.getElementById("close-list-modal");
      const mobileSearchInput = document.getElementById("mobile-search-input");
      const mobileClearRouteBtn = document.getElementById("mobile-clear-route-btn");

      // Open Filter Modal
      if (mobileFilterBtn && filterModal) {
        mobileFilterBtn.addEventListener("click", () => {
          openModal(filterModal, "filter", mobileFilterBtn);
          updateWaypointsUI();
        });
      }

      // Close Filter Modal
      if (closeFilterBtn && filterModal) {
        closeFilterBtn.addEventListener("click", () => closeModal(filterModal, "filter"));
      }

      // Open List Modal
      if (mobileListBtn && listModal) {
        mobileListBtn.addEventListener("click", () => openModal(listModal, "list", mobileListBtn));
      }

      // Close List Modal
      if (closeListBtn && listModal) {
        closeListBtn.addEventListener("click", () => closeModal(listModal, "list"));
      }

      // Esc closes whichever modal is open.
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        if (filterModal?.classList.contains("active")) closeModal(filterModal, "filter");
        else if (listModal?.classList.contains("active")) closeModal(listModal, "list");
      });

      // Scroll to Footer button
      if (mobileScrollFooterBtn) {
        mobileScrollFooterBtn.addEventListener("click", () => {
          const footer = document.querySelector("footer, .site-footer, [class*='footer']");
          if (footer) {
            footer.scrollIntoView({ behavior: "smooth" });
          } else {
            // Fallback: scroll to bottom of page
            window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
          }
        });
      }

      // Sync mobile search with desktop search
      if (mobileSearchInput && searchInput) {
        mobileSearchInput.addEventListener("input", (e) => {
          searchInput.value = e.target.value;
          currentSearch = e.target.value;
          filterMarkers();
        });
      }

      // Theme tags and route-mode buttons are wired in the main scope above
      // (setThemeFilter / setRouteMode) so they sync across desktop & mobile copies.

      // Mobile UX: picking a theme inside the filter modal auto-closes the modal
      // so the user immediately sees the filtered map.
      document.querySelectorAll(".mobile-modal-content .tag[data-theme]").forEach((tag) => {
        tag.addEventListener("click", () => {
          if (filterModal?.classList.contains("active")) closeModal(filterModal, "filter");
        });
      });

      // Mobile UX: pressing Enter in the search input also closes the filter modal.
      if (mobileSearchInput) {
        mobileSearchInput.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (filterModal?.classList.contains("active")) closeModal(filterModal, "filter");
          }
        });
      }

      // Mobile route clear button
      if (mobileClearRouteBtn) {
        mobileClearRouteBtn.addEventListener("click", () => {
          waypoints = [];
          clearFallbackPolyline();
          hideUserLocationMarker();
          if (routingControl) {
            map.removeControl(routingControl);
            routingControl = null;
          }
          initRoutingControl();
          updateWaypointsUI();
          clearRouteSummary();
        });
      }

      // Mobile entity list: clicking an entry closes the modal and opens its popup.
      document.querySelectorAll(".mobile-entity-item").forEach((item) => {
        item.addEventListener("click", (e) => {
          e.preventDefault();
          const lat = item.dataset.lat;
          const lng = item.dataset.lng;
          if (!isValidLatLng(lat, lng)) return;
          const target = allMarkers.find(({ entity }) => sameLocation(entity, { lat, lng }));
          if (!target) return;
          closeModal(listModal, "list");
          map.setView([parseFloat(lat), parseFloat(lng)], 17);
          stopBounce(target.marker);
          // Slight delay lets the modal close animation finish before the popup opens.
          setTimeout(() => target.marker.openPopup(), 300);
        });
      });

      // Navigation buttons in purple nav (responsive menu).
      // mobileFilterBtnNav and routeNotificationNav are declared in the main scope.
      const mobileListBtnNav = document.getElementById("mobile-list-btn-nav");
      const mobileScrollFooterBtnNav = document.getElementById("mobile-scroll-footer-btn-nav");

      function closePurpleNavMenu() {
        const mobileNav = document.querySelector('[x-data*="mobileMenu"]');
        if (mobileNav && mobileNav._x_dataStack && mobileNav._x_dataStack[0]) {
          mobileNav._x_dataStack[0].mobileMenuOpen = false;
        }
      }

      // Sync nav buttons with header buttons
      if (mobileFilterBtnNav && filterModal) {
        mobileFilterBtnNav.addEventListener("click", () => {
          openModal(filterModal, "filter", mobileFilterBtnNav);
          updateWaypointsUI();
          closePurpleNavMenu();
        });
      }

      if (mobileListBtnNav && listModal) {
        mobileListBtnNav.addEventListener("click", () => {
          openModal(listModal, "list", mobileListBtnNav);
          closePurpleNavMenu();
        });
      }

      if (mobileScrollFooterBtnNav) {
        mobileScrollFooterBtnNav.addEventListener("click", () => {
          const footer = document.querySelector("footer, .site-footer, [class*='footer']");
          if (footer) {
            footer.scrollIntoView({ behavior: "smooth" });
          } else {
            window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
          }
          closePurpleNavMenu();
        });
      }
    }

    // Initial paint (also runs on desktop): draws empty waypoint lists and hides notif dots.
    updateWaypointsUI();
  }
});
